import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { 
  Send, Loader2, Mail, Users, ChevronLeft, ChevronRight, 
  Eye, ListFilter, Check, AlertTriangle, Paperclip, X, FolderOpen, Upload 
} from 'lucide-react';
import { replaceVariables } from './VariablesHelper';
import VariablesHelper from './VariablesHelper';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export default function EmailMergeDialog({ 
  open, 
  onOpenChange, 
  lead, // Optional - for single lead mode
  preselectedListId, // Optional - preselect a specific list
  selectedLeads = [], // Optional - array of directly selected leads for bulk email
  templates = [],
  onSuccess
}) {
  // Mode: 'single' (to one lead), 'merge' (to lists), or 'bulk' (to selected leads)
  const [mode, setMode] = useState('single');
  
  // Lists
  const [lists, setLists] = useState([]);
  const [selectedLists, setSelectedLists] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);
  
  // Email form
  const [emailForm, setEmailForm] = useState({
    to: '',
    cc: '',
    bcc: '',
    subject: '',
    body: '',
    template_id: ''
  });
  
  // Attachments
  const [attachments, setAttachments] = useState([]);
  const [attachLibraryOpen, setAttachLibraryOpen] = useState(false);
  
  // Preview state
  const [previewMode, setPreviewMode] = useState(false);
  const [previewLeads, setPreviewLeads] = useState([]);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Sending state
  const [sending, setSending] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0, errors: [] });
  
  // Fetch lists when dialog opens
  useEffect(() => {
    if (open) {
      fetchLists();
      // Get lead email - check both email and work_email fields (CRM uses work_email, Franchise uses email)
      const leadEmail = lead?.work_email || lead?.email;
      
      // If selectedLeads provided (bulk mode from leads list page)
      if (selectedLeads && selectedLeads.length > 0) {
        setEmailForm(f => ({ ...f, to: '{email}' }));
        setMode('bulk');
      } else if (preselectedListId) {
        // Preselect the list and switch to merge mode with {email} variable
        setSelectedLists([preselectedListId]);
        setEmailForm(f => ({ ...f, to: '{email}' }));
        setMode('merge');
      } else if (leadEmail) {
        setEmailForm(f => ({ ...f, to: leadEmail }));
        setMode('single');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lead?.work_email, lead?.email, preselectedListId, selectedLeads?.length]);
  
  const fetchLists = async () => {
    setLoadingLists(true);
    try {
      const res = await axios.get(`${API}/franchise/lists`);
      setLists(res.data.lists || res.data || []);
    } catch (error) {
      console.error('Failed to fetch lists:', error);
    } finally {
      setLoadingLists(false);
    }
  };
  
  // Toggle list selection
  const toggleList = (listId) => {
    setSelectedLists(prev => {
      const newSelection = prev.includes(listId) 
        ? prev.filter(id => id !== listId)
        : [...prev, listId];
      
      // When lists are selected, set To field to {email} variable
      if (newSelection.length > 0) {
        setEmailForm(f => ({ ...f, to: '{email}' }));
        setMode('merge');
      } else {
        // When no lists selected, restore lead email if available
        const leadEmail = lead?.work_email || lead?.email;
        setEmailForm(f => ({ ...f, to: leadEmail || '' }));
        setMode('single');
      }
      
      return newSelection;
    });
  };
  
  // Apply template
  const handleTemplateChange = (templateId) => {
    setEmailForm(f => ({ ...f, template_id: templateId }));
    if (templateId) {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        // For preview, keep variables unresolved
        setEmailForm(f => ({ 
          ...f, 
          subject: template.subject || '', 
          body: template.content || '' 
        }));
      }
    }
  };
  
  // Insert variable
  const insertVariable = (variable, field) => {
    if (field === 'subject') {
      setEmailForm(f => ({ ...f, subject: f.subject + variable }));
    } else {
      setEmailForm(f => ({ ...f, body: f.body + variable }));
    }
  };
  
  // Load preview leads from selected lists
  const loadPreview = async () => {
    if (mode === 'single') {
      if (lead) {
        setPreviewLeads([lead]);
        setCurrentPreviewIndex(0);
        setPreviewMode(true);
      }
      return;
    }
    
    if (selectedLists.length === 0) {
      toast.error('Please select at least one list');
      return;
    }
    
    setLoadingPreview(true);
    try {
      // Fetch leads from selected lists
      const allLeads = [];
      for (const listId of selectedLists) {
        const res = await axios.get(`${API}/franchise/lists/${listId}`);
        const listData = res.data;
        if (listData.leads) {
          // The list already contains full lead data from backend
          for (const lead of listData.leads) {
            // Support both 'email' (Franchise) and 'work_email' (CRM) fields
            const leadEmail = lead.email || lead.work_email;
            if (leadEmail) {
              // Normalize to work_email for consistency in the dialog
              allLeads.push({ ...lead, work_email: leadEmail });
            }
          }
        }
      }
      
      // Remove duplicates by email
      const uniqueLeads = allLeads.reduce((acc, l) => {
        if (!acc.find(x => x.work_email === l.work_email)) {
          acc.push(l);
        }
        return acc;
      }, []);
      
      if (uniqueLeads.length === 0) {
        toast.error('No leads with email addresses found in selected lists');
        return;
      }
      
      setPreviewLeads(uniqueLeads);
      setCurrentPreviewIndex(0);
      setPreviewMode(true);
      toast.success(`Found ${uniqueLeads.length} leads with email addresses`);
    } catch (error) {
      toast.error('Failed to load preview');
      console.error(error);
    } finally {
      setLoadingPreview(false);
    }
  };
  
  // Get preview email for current lead
  const getPreviewEmail = () => {
    if (!previewLeads.length) return { subject: '', body: '', to: '' };
    const currentLead = previewLeads[currentPreviewIndex];
    return {
      to: currentLead.work_email || '',
      subject: replaceVariables(emailForm.subject, currentLead),
      body: replaceVariables(emailForm.body, currentLead),
      lead: currentLead
    };
  };
  
  // Navigate preview
  const nextPreview = () => {
    if (currentPreviewIndex < previewLeads.length - 1) {
      setCurrentPreviewIndex(i => i + 1);
    }
  };
  
  const prevPreview = () => {
    if (currentPreviewIndex > 0) {
      setCurrentPreviewIndex(i => i - 1);
    }
  };
  
  // Start sending process
  const startSending = () => {
    if (mode === 'single' && !emailForm.to) {
      toast.error('Please enter a recipient email');
      return;
    }
    
    if (mode === 'merge' && selectedLists.length === 0) {
      toast.error('Please select at least one list');
      return;
    }
    
    if (!emailForm.subject || !emailForm.body) {
      toast.error('Subject and body are required');
      return;
    }
    
    setConfirmSend(true);
  };
  
  // Send emails
  const sendEmails = async () => {
    setSending(true);
    setConfirmSend(false);
    
    try {
      if (mode === 'single') {
        // Single email
        const finalSubject = lead ? replaceVariables(emailForm.subject, lead) : emailForm.subject;
        const finalBody = lead ? replaceVariables(emailForm.body, lead) : emailForm.body;
        
        // Prepare attachments
        const processedAttachments = [];
        for (const att of attachments) {
          if (att.source === 'library') {
            processedAttachments.push({ document_id: att.id, filename: att.name });
          } else if (att.file) {
            const base64Content = await fileToBase64(att.file);
            processedAttachments.push({
              filename: att.name,
              content: base64Content,
              mime_type: att.file.type || 'application/octet-stream'
            });
          }
        }
        
        await axios.post(`${API}/communications/email`, {
          lead_id: lead?.id || 'manual',
          to: emailForm.to,
          cc: emailForm.cc ? emailForm.cc.split(',').map(e => e.trim()).filter(Boolean) : [],
          bcc: emailForm.bcc ? emailForm.bcc.split(',').map(e => e.trim()).filter(Boolean) : [],
          subject: finalSubject,
          body: finalBody,
          attachments: processedAttachments
        });
        
        toast.success('Email sent successfully');
        onSuccess?.();
        onOpenChange(false);
        resetForm();
      } else {
        // Batch emails - either to lists (merge mode) or selected leads (bulk mode)
        const leadsToEmail = mode === 'bulk' ? selectedLeads : previewLeads;
        setSendProgress({ sent: 0, total: leadsToEmail.length, errors: [] });
        
        const errors = [];
        let sent = 0;
        
        // Prepare attachments once
        const processedAttachments = [];
        for (const att of attachments) {
          if (att.source === 'library') {
            processedAttachments.push({ document_id: att.id, filename: att.name });
          } else if (att.file) {
            const base64Content = await fileToBase64(att.file);
            processedAttachments.push({
              filename: att.name,
              content: base64Content,
              mime_type: att.file.type || 'application/octet-stream'
            });
          }
        }
        
        for (const leadItem of leadsToEmail) {
          // Get email - franchise leads use 'email', CRM leads use 'work_email'
          const leadEmail = leadItem.work_email || leadItem.email;
          
          if (!leadEmail) {
            errors.push({ 
              email: `${leadItem.first_name} ${leadItem.last_name}`, 
              error: 'No email address' 
            });
            continue;
          }
          
          // Check if lead has DNE (Do Not Email) flag
          if (leadItem.dne) {
            errors.push({ 
              email: leadEmail, 
              error: 'Do Not Email flag is set' 
            });
            continue;
          }
          
          try {
            const finalSubject = replaceVariables(emailForm.subject, leadItem);
            const finalBody = replaceVariables(emailForm.body, leadItem);
            
            await axios.post(`${API}/communications/email`, {
              lead_id: leadItem.id,
              to: leadEmail,
              cc: emailForm.cc ? emailForm.cc.split(',').map(e => e.trim()).filter(Boolean) : [],
              bcc: emailForm.bcc ? emailForm.bcc.split(',').map(e => e.trim()).filter(Boolean) : [],
              subject: finalSubject,
              body: finalBody,
              attachments: processedAttachments
            });
            
            sent++;
            setSendProgress(p => ({ ...p, sent }));
          } catch (error) {
            errors.push({ 
              email: leadEmail, 
              error: error.response?.data?.detail || 'Failed to send' 
            });
          }
        }
        
        setSendProgress(p => ({ ...p, errors }));
        
        if (errors.length === 0) {
          toast.success(`Successfully sent ${sent} emails!`);
          onSuccess?.();
          onOpenChange(false);
          resetForm();
        } else {
          toast.warning(`Sent ${sent} emails, ${errors.length} failed`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };
  
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };
  
  const resetForm = () => {
    setEmailForm({ to: '', cc: '', bcc: '', subject: '', body: '', template_id: '' });
    setSelectedLists([]);
    setAttachments([]);
    setPreviewMode(false);
    setPreviewLeads([]);
    setCurrentPreviewIndex(0);
    setMode('single');
    setSendProgress({ sent: 0, total: 0, errors: [] });
  };
  
  // Handle file attachment
  const handleFileAttachment = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map(file => ({
      id: `local-${Date.now()}-${file.name}`,
      name: file.name,
      size: file.size,
      source: 'local',
      file: file
    }));
    setAttachments(prev => [...prev, ...newAttachments]);
  };
  
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };
  
  const preview = getPreviewEmail();
  const emailTemplates = templates.filter(t => t.type === 'email');
  
  // Calculate total recipients based on mode
  const totalRecipients = mode === 'bulk' 
    ? selectedLeads.length 
    : (mode === 'merge' ? previewLeads.length : 1);
  
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!sending) { onOpenChange(v); if (!v) resetForm(); } }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-indigo-600" />
            {previewMode ? 'Preview Email Merge' : 'Send Email'}
            {mode === 'bulk' && selectedLeads.length > 0 && (
              <Badge className="ml-2 bg-green-100 text-green-700">
                <Users className="h-3 w-3 mr-1" />
                {selectedLeads.length} lead{selectedLeads.length > 1 ? 's' : ''} selected
              </Badge>
            )}
            {mode === 'merge' && selectedLists.length > 0 && (
              <Badge className="ml-2 bg-indigo-100 text-indigo-700">
                <Users className="h-3 w-3 mr-1" />
                {selectedLists.length} list{selectedLists.length > 1 ? 's' : ''} selected
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        
        {/* Confirmation Dialog */}
        {confirmSend && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50 rounded-lg">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md">
              <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Confirm Send
              </h3>
              <p className="text-slate-600 mb-4">
                {mode === 'merge' 
                  ? `You are about to send ${previewLeads.length} personalized emails to leads from the selected lists.`
                  : mode === 'bulk'
                  ? `You are about to send ${selectedLeads.length} personalized emails to the selected leads.`
                  : `You are about to send an email to ${emailForm.to}.`
                }
              </p>
              <p className="text-sm text-slate-500 mb-4">
                This action cannot be undone.
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setConfirmSend(false)}>Cancel</Button>
                <Button onClick={sendEmails}>
                  <Send className="h-4 w-4 mr-2" />
                  Send {mode === 'merge' ? `${previewLeads.length} Emails` : mode === 'bulk' ? `${selectedLeads.length} Emails` : 'Email'}
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Sending Progress */}
        {sending && (
          <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center z-50 rounded-lg">
            <Loader2 className="h-12 w-12 animate-spin text-indigo-600 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Sending Emails...</h3>
            <p className="text-slate-600">
              {sendProgress.sent} of {sendProgress.total} sent
            </p>
            <div className="w-64 h-2 bg-slate-200 rounded-full mt-4 overflow-hidden">
              <div 
                className="h-full bg-indigo-600 transition-all duration-300"
                style={{ width: `${(sendProgress.sent / sendProgress.total) * 100}%` }}
              />
            </div>
            {sendProgress.errors.length > 0 && (
              <p className="text-red-500 text-sm mt-2">
                {sendProgress.errors.length} errors
              </p>
            )}
          </div>
        )}
        
        <div className="flex-1 overflow-hidden flex flex-col">
          {!previewMode ? (
            /* Compose Mode */
            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-4 pb-4">
                {/* Lead Name Header (single mode) */}
                {mode === 'single' && lead && (
                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                    <p className="text-sm text-slate-500">Sending email to</p>
                    <p className="font-semibold text-slate-900">{lead.first_name} {lead.last_name}</p>
                  </div>
                )}
                
                {/* Selected Leads Header (bulk mode) */}
                {mode === 'bulk' && selectedLeads.length > 0 && (
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                    <p className="text-sm text-green-700 font-medium">
                      Sending to {selectedLeads.length} selected lead{selectedLeads.length > 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      {selectedLeads.slice(0, 5).map(l => `${l.first_name} ${l.last_name}`).join(', ')}
                      {selectedLeads.length > 5 && ` and ${selectedLeads.length - 5} more...`}
                    </p>
                  </div>
                )}
                
                {/* Lists Selection - hide in bulk mode */}
                {mode !== 'bulk' && (
                <div className="border rounded-lg p-4 bg-slate-50">
                  <Label className="flex items-center gap-2 mb-3">
                    <ListFilter className="h-4 w-4" />
                    Send to Lists (Email Merge)
                  </Label>
                  {loadingLists ? (
                    <div className="flex items-center gap-2 text-slate-500">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading lists...
                    </div>
                  ) : lists.length === 0 ? (
                    <p className="text-sm text-slate-500">No lists available</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {lists.map(list => (
                        <button
                          key={list.id}
                          onClick={() => toggleList(list.id)}
                          className={`px-3 py-1.5 rounded-full text-sm border transition-colors flex items-center gap-1.5 ${
                            selectedLists.includes(list.id)
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-300'
                          }`}
                        >
                          {selectedLists.includes(list.id) && <Check className="h-3 w-3" />}
                          {list.name}
                          <span className={`text-xs ${selectedLists.includes(list.id) ? 'text-indigo-200' : 'text-slate-400'}`}>
                            ({list.lead_count || 0})
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                )}
                
                {/* TO Email (for single mode) */}
                {mode === 'single' && (
                  <div>
                    <Label>To *</Label>
                    <Input 
                      value={emailForm.to} 
                      onChange={(e) => setEmailForm(f => ({ ...f, to: e.target.value }))}
                      placeholder="recipient@example.com"
                      className="mt-1"
                      data-testid="email-to"
                    />
                  </div>
                )}
                
                {/* TO Email indicator for merge/bulk mode - show {email} variable */}
                {(mode === 'merge' && selectedLists.length > 0) || mode === 'bulk' ? (
                  <div>
                    <Label>To (per recipient)</Label>
                    <Input 
                      value="{email}"
                      readOnly
                      className="mt-1 bg-slate-100 text-slate-600 cursor-not-allowed"
                      data-testid="email-to-merge"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Emails will be sent to each lead's email address
                    </p>
                  </div>
                ) : null}
                
                {/* CC/BCC */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>CC</Label>
                    <Input 
                      value={emailForm.cc} 
                      onChange={(e) => setEmailForm(f => ({ ...f, cc: e.target.value }))}
                      placeholder="email1@example.com, email2@example.com"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>BCC</Label>
                    <Input 
                      value={emailForm.bcc} 
                      onChange={(e) => setEmailForm(f => ({ ...f, bcc: e.target.value }))}
                      placeholder="email@example.com"
                      className="mt-1"
                    />
                  </div>
                </div>
                
                {/* Template Selection */}
                <div>
                  <Label>Email Template</Label>
                  <Select 
                    value={emailForm.template_id || 'none'} 
                    onValueChange={(v) => handleTemplateChange(v === 'none' ? '' : v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select a template..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No template</SelectItem>
                      {emailTemplates.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Subject */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Subject *</Label>
                    <VariablesHelper onInsert={(v) => insertVariable(v, 'subject')} />
                  </div>
                  <Input 
                    value={emailForm.subject} 
                    onChange={(e) => setEmailForm(f => ({ ...f, subject: e.target.value }))}
                    placeholder="Email subject line..."
                    className="mt-1"
                    data-testid="email-subject"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Use variables like {'{FirstName}'} or {'{CompanyName}'} for personalization
                  </p>
                </div>
                
                {/* Body */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Body *</Label>
                    <VariablesHelper onInsert={(v) => insertVariable(v, 'body')} />
                  </div>
                  <Textarea 
                    value={emailForm.body} 
                    onChange={(e) => setEmailForm(f => ({ ...f, body: e.target.value }))}
                    placeholder="Email body..."
                    className="mt-1 min-h-[200px]"
                    data-testid="email-body"
                  />
                </div>
                
                {/* Attachments */}
                <div>
                  <Label>Attachments</Label>
                  <div className="flex items-center gap-2 flex-wrap mt-2">
                    {attachments.map((file, index) => (
                      <Badge key={index} variant="outline" className="gap-1 py-1.5">
                        <Paperclip className="h-3 w-3" />
                        {file.name}
                        <button onClick={() => removeAttachment(index)} className="ml-1 text-slate-400 hover:text-red-500">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => setAttachLibraryOpen(true)}>
                      <FolderOpen className="h-4 w-4 mr-1" /> From Library
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => document.getElementById('merge-file-input').click()}>
                      <Upload className="h-4 w-4 mr-1" /> From Computer
                    </Button>
                    <input 
                      id="merge-file-input"
                      type="file" 
                      multiple 
                      onChange={handleFileAttachment} 
                      className="hidden" 
                    />
                  </div>
                </div>
              </div>
            </ScrollArea>
          ) : (
            /* Preview Mode */
            <div className="flex-1 flex flex-col">
              {/* Preview Navigation */}
              <div className="flex items-center justify-between py-3 border-b">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={prevPreview}
                  disabled={currentPreviewIndex === 0}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>
                <div className="text-sm text-slate-600">
                  Previewing <strong>{currentPreviewIndex + 1}</strong> of <strong>{previewLeads.length}</strong>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={nextPreview}
                  disabled={currentPreviewIndex === previewLeads.length - 1}
                >
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
              
              {/* Preview Content */}
              <ScrollArea className="flex-1">
                <div className="p-4 space-y-4">
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <div>
                      <span className="text-xs text-slate-500 uppercase tracking-wide">To:</span>
                      <p className="font-medium">{preview.to}</p>
                    </div>
                    {preview.lead && (
                      <div>
                        <span className="text-xs text-slate-500 uppercase tracking-wide">Lead:</span>
                        <p className="font-medium">
                          {preview.lead.first_name} {preview.lead.last_name}
                          {preview.lead.company_name && <span className="text-slate-500"> • {preview.lead.company_name}</span>}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <span className="text-xs text-slate-500 uppercase tracking-wide">Subject:</span>
                    <p className="font-medium text-lg mt-1">{preview.subject}</p>
                  </div>
                  
                  <div>
                    <span className="text-xs text-slate-500 uppercase tracking-wide">Body:</span>
                    <div className="mt-2 p-4 bg-white border rounded-lg whitespace-pre-wrap">
                      {preview.body}
                    </div>
                  </div>
                  
                  {attachments.length > 0 && (
                    <div>
                      <span className="text-xs text-slate-500 uppercase tracking-wide">Attachments:</span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {attachments.map((att, i) => (
                          <Badge key={i} variant="outline" className="gap-1">
                            <Paperclip className="h-3 w-3" />
                            {att.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
        
        <DialogFooter className="mt-4 pt-4 border-t">
          {previewMode ? (
            <>
              <Button variant="outline" onClick={() => setPreviewMode(false)}>
                Back to Edit
              </Button>
              <Button onClick={startSending} disabled={sending}>
                <Send className="h-4 w-4 mr-2" />
                Send {totalRecipients > 1 ? `${totalRecipients} Emails` : 'Email'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => { onOpenChange(false); resetForm(); }}>
                Cancel
              </Button>
              {mode === 'merge' || selectedLists.length > 0 ? (
                <Button 
                  onClick={loadPreview} 
                  disabled={loadingPreview || (mode === 'merge' && selectedLists.length === 0)}
                >
                  {loadingPreview ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Eye className="h-4 w-4 mr-2" />
                  )}
                  Preview & Send
                </Button>
              ) : (
                <Button 
                  onClick={startSending}
                  disabled={!emailForm.to || !emailForm.subject || !emailForm.body}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Email
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
